import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import api from '../../lib/api.js'
import { formatDate } from '../../lib/format.js'

/*
 * Peppol participant panel — inventory §9 / customer & supplier details.
 * Shows the participant id + verification status and lets the user re-verify
 * via POST /{entity}/{id}/peppol-lookup. `notFoundTone` differs by entity
 * (amber for customers, red for suppliers).
 */
export default function PeppolPanel({ entity, party, notFoundTone = 'warning', onVerified }) {
  const { t } = useTranslation()
  const [verifying, setVerifying] = useState(false)
  const [local, setLocal] = useState(party)

  const p = local
  const configured = p?.peppol_participant_id
  if (!configured) {
    return (
      <div style={{ color: 'var(--color-warning)', fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="info" size={16} /> {t('parties.peppol.notConfigured')}
      </div>
    )
  }

  let badge = { tone: 'neutral', label: t('parties.peppol.pending') }
  if (p.peppol_found === true && p.peppol_verified_at) badge = { tone: 'success', label: t('parties.peppol.verified') }
  else if (p.peppol_found === false && p.peppol_verified_at) badge = { tone: notFoundTone, label: t('parties.peppol.notFound') }

  const verify = async () => {
    setVerifying(true)
    try {
      const { data } = await api.post(`${entity}/${p.id}/peppol-lookup`)
      const updated = { ...p, peppol_found: data?.found, peppol_verified_at: data?.verified_at || new Date().toISOString() }
      setLocal(updated)
      onVerified?.(updated)
    } catch { /* surfaced by badge staying pending */ } finally { setVerifying(false) }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Badge tone={badge.tone}>{badge.label}</Badge>
        {p.peppol_verified_at && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('parties.peppol.lastVerified')} {formatDate(p.peppol_verified_at)}</span>}
      </div>
      {p.peppol_scheme_id && <Info label={t('parties.peppol.schemeId')} value={p.peppol_scheme_id} />}
      <Info label={t('parties.peppol.participantId')} value={`${p.peppol_scheme_id || ''}${p.peppol_scheme_id ? '::' : ''}${p.peppol_participant_id}`} mono />
      <div style={{ marginTop: 12 }}>
        <Button variant="secondary" icon="link" loading={verifying} onClick={verify}>{t('parties.peppol.verify')}</Button>
      </div>
    </div>
  )
}

function Info({ label, value, mono }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 13.5, color: 'var(--color-ink)', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
    </div>
  )
}
